import {Component} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {PayrollHttpService} from './payroll-http.service';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {AlertLevel} from '../../../enums/alert-level';

/**
 * Lets a signed-in external accountant redeem a payroll invitation code. On success we enter the just-unlocked
 * company (`POST /auth/context/enter`, company scope, management view) and reload into its view. Embedded in the
 * profile page. If entering fails, we fall back to pointing at the workspace switcher.
 *
 * This is the path for somebody who ALREADY has an account. An invitee who does not cannot reach this screen at
 * all — since #279 an account is created BY redeeming, so signing in first is impossible. They go through the
 * invite link instead ({@link InviteLandingComponent} at /invite), which is what the invitation email points at.
 */
@Component({
  selector: 'company-payroll-redeem',
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <h2 class="text-sm font-semibold text-gray-900">Einladungscode einlösen</h2>
      <!-- Deliberately does NOT name a role: this box posts to /auth/payroll/redeem, a thin wrapper over
           the generic grant redemption, so it accepts ANY invitation code — a payroll invitation, any
           other role grant, or an employee link. Naming only Lohnbuchhalter told everyone else the box
           was not for them, which is how it was reported. Enumerating the kinds would be the same
           mistake in a longer sentence: the person holding a code does not know which kind it is. -->
      <p class="mt-1 text-xs text-gray-400">Sie haben einen Einladungscode erhalten? Lösen Sie ihn hier ein.</p>

      <div class="mt-3 flex flex-col gap-2 sm:flex-row">
        <input type="text" [(ngModel)]="code" placeholder="Code einfügen" aria-label="Einladungscode"
               class="flex-1 rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm focus:border-theme-primary-500 focus:outline-none focus:ring-1 focus:ring-theme-primary-500"/>
        <button type="button" (click)="redeem()" [disabled]="busy || !code"
                class="rounded-md bg-theme-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-theme-primary-900 disabled:opacity-50">
          Einlösen
        </button>
      </div>

      @if (unlocked) {
        <div class="mt-3 rounded-lg bg-green-50 p-3 text-sm text-green-800">
          Zugriff auf <span class="font-semibold">{{ unlocked }}</span> freigeschaltet. Wechseln Sie unten links
          über den Arbeitsbereich zu dieser Firma.
        </div>
      }
    </div>
  `
})
export class PayrollRedeemComponent {
  protected code = '';
  protected busy = false;
  protected unlocked: string | null = null;

  constructor(private http: PayrollHttpService, private gas: GlobalAlertService, private auth: AuthenticationService) {
  }

  protected redeem(): void {
    if (this.busy || !this.code) return;
    this.busy = true;
    this.http.redeem(this.code.trim()).subscribe({
      next: result => {
        this.code = '';
        this.gas.createAndShow('Erfolg', `Zugriff auf ${result.companyName} freigeschaltet.`, AlertLevel.success);
        // Enter the just-unlocked company and reload into its view. The grant was created by the redeem a moment
        // ago, so `Enter` recomputes it and matches; on failure, fall back to the manual-switch hint.
        this.auth.enterCompany(result.companyId).subscribe({
          next: () => window.open('/', '_self'),
          error: () => {
            this.unlocked = result.companyName;
            this.busy = false;
          }
        });
      },
      error: () => {
        this.gas.createAndShow('Fehler', 'Code ungültig, abgelaufen oder bereits eingelöst.', AlertLevel.error);
        this.busy = false;
      }
    });
  }
}
